import { useEffect } from 'react'
import { enhancedTmsApiService } from '#api/enhancements/enhancedTmsApiService'
import type { CavaResponse } from '#api/tmsApiService'
import { useAppDispatch } from '#app/store/root'
import { type LocalConfigurationId, loadTechnicalData, truckConfigurationSlice } from '#app/store/slices/configurationSlice'
import { useActiveConfiguration } from '#pages/Configuration/components/useActiveConfiguration'
import type { AppMiddleware } from '../configurationRequestMiddleware'

export type ConfigurationTechnicalData = ReturnType<typeof mapCavaResponse>
const mapCavaResponse = (cavaResponse: CavaResponse) => {
  return {
    dvs: cavaResponse?.content?.dvs,
    n3g: cavaResponse?.content?.n3g,
    vehicleDimension: cavaResponse?.content?.vehicleDimension,
    turningRadius: cavaResponse?.content?.turningRadius,
  }
}

export const useTechnicalDataApi = () => {
  const configuration = useActiveConfiguration()

  const dispatch = useAppDispatch()
  useEffect(() => {
    // do not try to load technical data while there are still modifications going on, this causes poor timing
    // where any dimensions may not line up correctly
    if (configuration?.history?.find((x) => x.status === 'queued' || x.status === 'pending')) {
      return
    }
    if (!configuration || (configuration?.technicalData && configuration.technicalData.version >= configuration.version)) {
      return
    }
    dispatch(loadTechnicalData(configuration.id))
  }, [configuration, dispatch])
}

export const configurationTechnicalDataListener = (middleware: AppMiddleware) => {
  const handledRequests: Partial<Record<LocalConfigurationId, number>> = {}

  middleware.startListening({
    type: loadTechnicalData.type,
    effect: async (action, listenerApi) => {
      if (!loadTechnicalData.match(action)) {
        return
      }
      const state = listenerApi.getState()
      const configuration = state.truckConfigurations.configurations[action.payload]
      if (!configuration?.uuid) {
        return
      }
      // check that not another handler already is serving that request
      const priorHandledRequests = handledRequests[configuration.id]
      if (priorHandledRequests !== undefined && priorHandledRequests >= configuration.version) {
        return
      }
      handledRequests[configuration.id] = configuration.version

      listenerApi.dispatch(
        truckConfigurationSlice.actions._addTechnicalData({
          id: configuration.id,
          technicalData: {
            status: 'requested',
            version: configuration.version,
          },
        }),
      )

      const request = listenerApi.dispatch(
        enhancedTmsApiService.endpoints.postApiCavaByUuid.initiate({
          uuid: configuration.uuid,
          cavaRequest: {
            includeDimensions: true,
            includeN3g: true,
            includeDvs: true,
            includeTurningRadii: true,
          },
        }),
      )
      try {
        const response = await request.unwrap()
        listenerApi.dispatch(
          truckConfigurationSlice.actions._addTechnicalData({
            id: configuration.id,
            technicalData: {
              version: configuration.version,
              status: 'loaded',
              data: mapCavaResponse(response),
            },
          }),
        )
      } catch {
        listenerApi.dispatch(
          truckConfigurationSlice.actions._addTechnicalData({
            id: configuration.id,
            technicalData: {
              version: configuration.version,
              status: 'error',
            },
          }),
        )
      }
    },
  })
}
